import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';

type FayeStoreSplashProps = {
  open: boolean;
  storeName: string;
  aisleName?: string | null;
  storeAddress?: string | null;
  onComplete: () => void;
};

export default function FayeStoreSplash({ open, storeName, aisleName, storeAddress, onComplete }: FayeStoreSplashProps) {
  React.useEffect(() => {
    if (!open) return;

    const timeoutId = window.setTimeout(() => {
      onComplete();
    }, 1150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open, onComplete]);

  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[11000] flex h-screen min-h-screen w-screen items-center justify-center bg-background/68 px-6 backdrop-blur-xl supports-[height:100dvh]:h-dvh supports-[height:100dvh]:min-h-dvh"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <motion.div
            className="relative w-full max-w-sm overflow-hidden rounded-[32px] border border-primary/15 bg-gradient-to-br from-background via-background to-primary/10 p-6 text-center shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_24px_80px_rgba(15,23,42,0.28)]"
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="absolute inset-0 opacity-70"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%'] }}
              transition={{ duration: 1.1, ease: 'linear' }}
              style={{
                backgroundImage: 'radial-gradient(circle at top, rgba(99,102,241,0.22), transparent 44%), radial-gradient(circle at bottom right, rgba(99,102,241,0.14), transparent 36%)',
                backgroundSize: '160% 160%',
              }}
            />

            <div className="relative flex flex-col items-center gap-4">
              <div className="space-y-2">
                <motion.div
                  className="text-2xl font-black leading-tight text-foreground"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, duration: 0.24, ease: 'easeOut' }}
                >
                  {storeName}
                </motion.div>
                {aisleName ? (
                  <motion.div
                    className="mx-auto inline-flex max-w-[18rem] items-center justify-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold tracking-wide text-primary"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12, duration: 0.24, ease: 'easeOut' }}
                  >
                    Sección {aisleName}
                  </motion.div>
                ) : null}
                {storeAddress && !aisleName ? (
                  <motion.div
                    className="mx-auto max-w-[18rem] text-sm leading-snug text-muted-foreground"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: aisleName ? 0.2 : 0.14, duration: 0.24, ease: 'easeOut' }}
                  >
                    {storeAddress}
                  </motion.div>
                ) : null}
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
    , document.body
  );
}